import React from 'react';
import { AlertTriangle, Clock } from 'lucide-react';

interface SessionTimeoutWarningProps {
  isVisible: boolean;
  timeRemaining: number; // seconds
  onExtendSession: () => void;
  onLogoutNow: () => void;
}

/**
 * Modal warning displayed when user session is about to expire due to inactivity
 */
export function SessionTimeoutWarning({
  isVisible,
  timeRemaining,
  onExtendSession,
  onLogoutNow,
}: SessionTimeoutWarningProps) {
  if (!isVisible) return null;

  const minutes = Math.floor(timeRemaining / 60);
  const seconds = timeRemaining % 60;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      
      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* Warning Header */}
        <div className="bg-amber-500 px-6 py-4 flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 text-white" />
          <h2 className="text-xl font-bold text-white">
            Session Timeout Warning
          </h2>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          {/* Countdown Display */}
          <div className="flex items-center justify-center mb-6">
            <div className="bg-gray-100 rounded-lg px-6 py-4 flex items-center gap-3">
              <Clock className="w-8 h-8 text-amber-500" />
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900 tabular-nums">
                  {minutes.toString().padStart(2, '0')}:{seconds.toString().padStart(2, '0')}
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  Time remaining
                </div>
              </div>
            </div>
          </div>

          {/* Message */}
          <div className="text-center mb-6">
            <p className="text-gray-700 leading-relaxed">
              Your session will expire due to inactivity. You will be automatically logged out for security reasons.
            </p>
            <p className="text-gray-600 text-sm mt-2">
              Click "Stay Logged In" to continue your session.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onLogoutNow}
              className="flex-1 px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg transition-colors"
            >
              Logout Now
            </button>
            <button
              type="button"
              onClick={onExtendSession}
              className="flex-1 px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-colors shadow-lg shadow-emerald-500/30"
            >
              Stay Logged In
            </button>
          </div>
        </div>

        {/* Bottom Info Bar */}
        <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
          <p className="text-xs text-gray-600 text-center">
            🔒 This timeout protects your account when you're away from your device
          </p>
        </div>
      </div>
    </div>
  );
}

